import { useRef, useState, useEffect, useCallback } from 'react';
import s from './CustomScroll.module.scss';

const CustomScroll = ({ children, scrollRef, className = '', style = {} }) => {
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [thumbTop, setThumbTop] = useState(0);
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight) {
      setThumbHeight(0);
      return;
    }
    const ratio = clientHeight / scrollHeight;
    const height = Math.max(ratio * clientHeight, 32);
    const maxTop = clientHeight - height;
    const top = (scrollTop / (scrollHeight - clientHeight)) * maxTop;
    setThumbHeight(height);
    setThumbTop(top);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateThumb, { passive: true });
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    updateThumb();

    const onWheel = (e) => {
      e.preventDefault();
      el.scrollTop += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('scroll', updateThumb);
      el.removeEventListener('wheel', onWheel);
      ro.disconnect();
    };
  }, [scrollRef, updateThumb]);

  const onThumbMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartY.current = e.clientY;
    dragStartScrollTop.current = scrollRef.current.scrollTop;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    const el = scrollRef.current;
    const trackHeight = trackRef.current.clientHeight;
    const thumbH = thumbRef.current.clientHeight;
    const delta = e.clientY - dragStartY.current;
    const maxScrollTop = el.scrollHeight - el.clientHeight;
    const maxThumbTop = trackHeight - thumbH;
    const newScrollTop = dragStartScrollTop.current + (delta / maxThumbTop) * maxScrollTop;
    el.scrollTop = Math.max(0, Math.min(newScrollTop, maxScrollTop));
  }, [scrollRef]);

  const onMouseUp = useCallback(() => {
    isDragging.current = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);

  const onTrackClick = (e) => {
    if (e.target === thumbRef.current) return;
    const el = scrollRef.current;
    const track = trackRef.current;
    const trackRect = track.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const thumbH = thumbRef.current.clientHeight;
    const maxTop = track.clientHeight - thumbH;
    const ratio = Math.max(0, Math.min((clickY - thumbH / 2) / maxTop, 1));
    el.scrollTop = ratio * (el.scrollHeight - el.clientHeight);
  };

  return (
    <div className={s.wrap + (className ? ' ' + className : '')} style={style}>
      <div
        ref={scrollRef}
        className={s.content}
      >
        {children}
      </div>
      {thumbHeight > 0 && (
        <div ref={trackRef} className={s.track} onClick={onTrackClick}>
          <div
            ref={thumbRef}
            className={s.thumb}
            style={{ height: thumbHeight, transform: `translateY(${thumbTop}px)` }}
            onMouseDown={onThumbMouseDown}
          />
        </div>
      )}
    </div>
  );
};

export default CustomScroll;
